'use client';

import { useTranslations } from 'next-intl';
import { useCallback } from 'react';

import { RateMentorCard } from '@ui/components/features/review/rateMentorCard';
import { useRateBooking } from '@website/context/RateBookingContext';
import { useActivityBookings } from '@website/hooks/useActivityBookings';
import { useCurrentUser } from '@website/hooks/useCurrentUser';
import { useRateMentorCardResetKey } from '@website/hooks/useRateMentorCardResetKey';
import { useReviews } from '@website/hooks/useReviews';

interface WriteReviewSectionProps {
  activityId: string;
  activityTitle?: string;
  professionalName?: string;
  professionalId?: string;
}

export default function WriteReviewSection({
  activityId,
  activityTitle,
  professionalName,
  professionalId
}: WriteReviewSectionProps) {
  const t = useTranslations('ActivityDetail.ratings');
  const { data: user } = useCurrentUser();
  const isAuthenticated = !!user;
  const isOwner = !!user && user.isProfessional && !!professionalId && user.id === professionalId;
  const { data: reviewsData, isPending: reviewsPending } = useReviews(activityId, { limit: 50 });
  const hasReviewed = !!user && (reviewsData?.reviews.some((r) => r.userId === user.id) ?? false);
  const { pastBookings, isLoading: isBookingsLoading } = useActivityBookings(activityId, user);
  const hasCompletedBooking = pastBookings.length > 0;
  const { openRateBooking } = useRateBooking();
  const rateMentorCardKey = useRateMentorCardResetKey();

  const handleWriteReview = useCallback(
    (rating?: number) => {
      openRateBooking(
        {
          bookingId: '',
          activityId,
          activityTitle: activityTitle ?? '',
          professionalName: professionalName ?? '',
          bookingDate: '',
          timeSlot: '',
          ...(typeof rating === 'number' && rating >= 1 && rating <= 5 ? { initialRating: rating } : {})
        },
        { skipCheck: true }
      );
    },
    [openRateBooking, activityId, activityTitle, professionalName]
  );

  if (!isAuthenticated || isOwner || hasReviewed || !hasCompletedBooking) return null;

  if (reviewsPending || isBookingsLoading) return null;

  return (
    <section className="w-full">
      <RateMentorCard
        key={rateMentorCardKey}
        onWriteReview={handleWriteReview}
        title={t('howWasYourExperience')}
        subtitle={t('tellUsAboutMentor')}
        ctaText={t('writeReview')}
        className="md:max-w-sm"
      />
    </section>
  );
}
